import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { generateShiftReport, getShiftReports, getStores } from '../api/api';
import { FileText, Download, Building2, Calendar, Clock, Filter, Eye } from 'lucide-react';

const Reports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedStore, setSelectedStore] = useState('');
  const [formData, setFormData] = useState({
    shift_date: new Date().toISOString().split('T')[0],
    shift_number: 1,
    store_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = ['admin', 'manager', 'supervisor', 'admin_assistant'].includes(user?.role);

  const loadStores = useCallback(async () => {
    try {
      const data = await getStores();
      setStores(data);
      if (user?.store_id) {
        setSelectedStore(user.store_id);
        setFormData(prev => ({ ...prev, store_id: user.store_id }));
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  }, [user?.store_id]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getShiftReports(selectedStore || null);
      setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    loadStores();
    loadReports();
  }, [loadStores, loadReports]);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const storeId = formData.store_id || user?.store_id;
      if (!storeId && !isAdmin) {
        setError('Debe seleccionar una tienda');
        setSubmitting(false);
        return;
      }
      
      const report = await generateShiftReport(formData.shift_number, formData.shift_date, storeId);
      setSuccess('¡Reporte generado exitosamente!');
      setSelectedReport(report);
      setShowGenerateForm(false);
      loadReports();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al generar el reporte');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      utcDateString = dateString + 'Z';
    }
    const date = new Date(utcDateString);
    return date.toLocaleString('es-GT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const canGenerate = ['store_manager', 'admin', 'manager', 'supervisor'].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-purple-800 rounded-xl shadow-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText size={28} />
              Reportes de Turnos
            </h1>
            <p className="text-slate-300 mt-1">Genera y consulta reportes de cada turno de trabajo</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Filtro de Tienda para Admin */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Building2 size={18} />
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="">Todas las tiendas</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
            )}

            {canGenerate && (
              <button
                onClick={() => setShowGenerateForm(!showGenerateForm)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <FileText size={20} />
                <span>Generar Reporte</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center space-x-3">
          <span className="text-2xl">❌</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center space-x-3">
          <span className="text-2xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}

      {/* Formulario Generar Reporte */}
      {showGenerateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={24} className="text-purple-600" />
            Generar Reporte de Turno
          </h2>
          <form onSubmit={handleGenerateReport} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Selector de Tienda para Admin */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    🏪 Tienda
                  </label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required={isAdmin}
                  >
                    <option value="">Seleccionar tienda...</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  📅 Fecha
                </label>
                <input
                  type="date"
                  value={formData.shift_date}
                  onChange={(e) => setFormData({ ...formData, shift_date: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  🕐 Número de Turno
                </label>
                <select
                  value={formData.shift_number}
                  onChange={(e) => setFormData({ ...formData, shift_number: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="1">Turno 1 (5:00am - 1:00pm)</option>
                  <option value="2">Turno 2 (1:00pm - 9:00pm)</option>
                  <option value="3">Turno 3 (9:00pm - 5:00am)</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-slate-400 font-semibold"
              >
                {submitting ? 'Generando...' : 'Generar Reporte'}
              </button>
              <button
                type="button"
                onClick={() => setShowGenerateForm(false)}
                className="px-6 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reporte Seleccionado Detallado */}
      {selectedReport && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Reporte Detallado</h2>
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 flex items-center space-x-2"
              >
                <Download size={20} />
                <span>Imprimir</span>
              </button>
              <button
                onClick={() => setSelectedReport(null)}
                className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400"
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">🏪 Tienda</p>
              <p className="text-lg font-bold text-slate-800">{selectedReport.store_name}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">📅 Fecha</p>
              <p className="text-lg font-bold text-slate-800">{selectedReport.shift_date}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">🕐 Turno</p>
              <p className="text-lg font-bold text-slate-800">Turno {selectedReport.shift_number}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">👤 Generado por</p>
              <p className="text-lg font-bold text-slate-800">{selectedReport.generated_by_name}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-xl text-white">
              <p className="text-sm font-medium opacity-90">Total Ventas</p>
              <p className="text-3xl font-bold">Q{selectedReport.total_sales?.toFixed(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 p-5 rounded-xl text-white">
              <p className="text-sm font-medium opacity-90">Total Gastos</p>
              <p className="text-3xl font-bold">Q{selectedReport.total_expenses?.toFixed(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-xl text-white">
              <p className="text-sm font-medium opacity-90">Balance Neto</p>
              <p className="text-3xl font-bold">Q{selectedReport.net_balance?.toFixed(2)}</p>
            </div>
          </div>

          {/* Ventas del Turno */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3">💰 Ventas del Turno</h3>
            {selectedReport.sales?.length === 0 ? (
              <p className="text-slate-500 bg-slate-50 p-4 rounded-lg">No hay ventas en este turno</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Vendedor</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Método</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {selectedReport.sales?.map((sale) => (
                      <tr key={sale.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{sale.seller_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                            {sale.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">Q{sale.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Gastos del Turno */}
          <div>
            <h3 className="text-lg font-bold text-slate-800 mb-3">📤 Gastos del Turno</h3>
            {selectedReport.expenses?.length === 0 ? (
              <p className="text-slate-500 bg-slate-50 p-4 rounded-lg">No hay gastos en este turno</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Descripción</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {selectedReport.expenses?.map((expense) => (
                      <tr key={expense.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-semibold">
                            {expense.expense_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{expense.description}</td>
                        <td className="px-4 py-3 text-sm font-bold text-red-600">Q{expense.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historial de Reportes */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Calendar size={20} />
            Historial de Reportes
            {selectedStore && stores.find(s => s.id === selectedStore) && (
              <span className="text-sm font-normal text-slate-300">
                - {stores.find(s => s.id === selectedStore)?.name}
              </span>
            )}
          </h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-3 text-slate-500">Cargando reportes...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <FileText size={48} className="mx-auto mb-3 text-slate-300" />
            <p>No hay reportes generados</p>
            {isAdmin && !selectedStore && (
              <p className="text-sm mt-2">Selecciona una tienda para ver sus reportes</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-100">
                  {isAdmin && <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Tienda</th>}
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Turno</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Ventas</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Gastos</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Neto</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Generado Por</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {reports.map((report, index) => (
                  <tr key={report.id} className={`hover:bg-purple-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold">
                          {report.store_name}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {report.shift_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg font-semibold">
                        Turno {report.shift_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      Q{report.total_sales?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                      Q{report.total_expenses?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                      Q{report.net_balance?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {report.generated_by_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-1 text-xs font-semibold"
                      >
                        <Eye size={14} />
                        Ver Detalles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
